import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime, todayStr } from "../lib/format";
import { fmtTime } from "../lib/driverDelivery";
import { deliveryCompleteBlocker } from "../lib/sitLocal";
import { DestCard, PlannerCrew, ReadField, SaveButton, SectionLabel, SalespersonField } from "./fields";
import { DeliveryTypeField, SitLocalSection } from "./SitLocalSection";
import { PostEscalationNotes, RaiseEscalationPanel } from "./EscalationResolution";

/** The driver the job was handed to (read-only here; it is set from the board or In Transit). */
const DriverBlock = ({ driverDelivery }) => (
  <div className="driver-info" style={{ marginTop: 0, marginBottom: 12 }}>
    <div className="di-row">
      <Icon name="clock" />
      <strong>
        ETA {fmtDate(driverDelivery.etaDate)}
        {driverDelivery.etaTime ? " \u00b7 " + fmtTime(driverDelivery.etaTime) : ""}
      </strong>
    </div>
    {driverDelivery.driverName ? (
      <div className="di-row">
        <Icon name="truck" />
        Driver/Trip: {driverDelivery.driverName}
      </div>
    ) : null}
    {driverDelivery.note ? <div className="di-note">{driverDelivery.note}</div> : null}
  </div>
);

/** LD "Delivery Day" tab (s4). */
export const TabDeliveryDay = ({ job, escalations, actions }) => {
  const jobEsc = escalations.filter((e) => e.jobId === job.id);
  const openCount = jobEsc.filter((e) => e.status === "open").length;
  const [escFormOpen, setEscFormOpen] = useState(false);
  const [remFormOpen, setRemFormOpen] = useState(false);
  const [remText, setRemText] = useState("");
  const [remDate, setRemDate] = useState("");
  const [feedback, setFeedback] = useState(job.inProgressFeedback || "");
  const [completeError, setCompleteError] = useState("");
  const completeBlocker = deliveryCompleteBlocker(job, feedback);
  const openBalance =
    job.openBalance != null ? job.openBalance : Math.round(job.price * 0.4);

  return (
    <>
      <SectionLabel icon="truck">Delivery Day</SectionLabel>
      <DestCard
        icon="truck"
        label="Delivery Address"
        address={job.moveTo}
        stats={[
          ["Move date", fmtDate(job.moveDate)],
          [
            "Delivery window",
            `${fmtDate(job.deliveryWindowStart)} – ${fmtDate(job.deliveryWindowEnd)}`,
          ],
          [
            "Status",
            job.status === "ld-confirmed-weekly"
              ? "Confirmed for delivery"
              : "In progress",
          ],
        ]}
      />

      <DeliveryTypeField job={job} actions={actions} />
      {job.driverDelivery ? <DriverBlock driverDelivery={job.driverDelivery} /> : null}

      <SectionLabel icon="activity">Salesperson</SectionLabel>
      <SalespersonField job={job} />

      <SectionLabel icon="activity">Assigned Crew</SectionLabel>
      <PlannerCrew job={job} />

      <SectionLabel icon="alert">Escalations</SectionLabel>
      <div
        className="dest-card"
        style={{
          background: jobEsc.length
            ? "linear-gradient(135deg, var(--danger-light) 0%, #fff 65%)"
            : "linear-gradient(135deg, var(--success-light) 0%, #fff 65%)",
          borderColor: jobEsc.length ? "#f3c6c6" : "#bfe6c9",
        }}
      >
        <div className="dest-card-head" style={{ marginBottom: jobEsc.length ? 12 : 0 }}>
          <div
            className="dest-card-icon"
            style={{
              background: jobEsc.length ? "var(--danger)" : "var(--success)",
              boxShadow: "none",
            }}
          >
            <Icon name={jobEsc.length ? "alert" : "check"} />
          </div>
          <div>
            <div className="dest-card-label">Previous Escalation</div>
            <div className="dest-card-address">
              {jobEsc.length ? `Yes — ${openCount} currently open` : "No"}
            </div>
          </div>
        </div>
        {jobEsc.map((e) => (
          <React.Fragment key={e.id}>
            <div className="note-item">
              <span className={`badge ${e.status}`}>
                <span className="bdot" />
                {e.type}
              </span>{" "}
              {e.note}
              <div className="meta">{fmtDateTime(e.createdAt)}</div>
            </div>
            <div className="chat-note">
              <Icon name="msg" />
              posted to Ops/Sales group chat
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="toolbar-row">
        <button type="button" className="btn small" aria-expanded={escFormOpen} onClick={() => setEscFormOpen((v) => !v)}>
          <Icon name="alert" />
          {escFormOpen ? "Hide Raise Escalation" : "Raise Escalation"}
        </button>
      </div>
      {/* The same panel and notes as In Progress, per the prototype (Asana 1220099429071097, item 6). */}
      {escFormOpen ? <RaiseEscalationPanel job={job} actions={actions} stage="delivery" /> : null}
      <PostEscalationNotes job={job} escalations={jobEsc} actions={actions} />

      {(job.s2Notes && job.s2Notes.deliveryType) === "sit-local" ? (
        <SitLocalSection job={job} actions={actions} key={`sit-${job.id}`} />
      ) : null}

      <SectionLabel icon="note">Feedback from the Client</SectionLabel>
      <div className="field">
        <textarea
          rows={4}
          aria-label="Feedback from the client"
          placeholder="Type notes from the client's feedback..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <div className="toolbar-row" style={{ marginTop: 6 }}>
          <SaveButton
            idleIcon="note"
            label="Save feedback"
            disabled={feedback.trim() === "" || feedback === (job.inProgressFeedback || "")}
            onSave={() => actions.setJobField(job.id, "inProgressFeedback", feedback)}
          />
        </div>
      </div>

      {/* Mark Completed sends the job to Post Move (Miles, Asana 1219141409140286). The control
          against a one-click close: it unlocks only once the delivery visibly happened and the
          client's feedback above is written. */}
      <SectionLabel icon="check">Delivery Status</SectionLabel>
      <div className="toolbar-row" style={{ marginBottom: 14, alignItems: "center" }}>
        {job.deliveryDayCompletedAt ? (
          <span className="badge resolved">
            <span className="bdot" />
            Completed · {fmtDateTime(job.deliveryDayCompletedAt)} — moved to Post Move
          </span>
        ) : (
          <>
            <button
              type="button"
              className="btn success"
              disabled={!!completeBlocker}
              title={completeBlocker || undefined}
              onClick={() => {
                const reason = actions.markDeliveryDayCompleted(job.id, feedback);
                if (reason) setCompleteError(reason);
              }}
            >
              <Icon name="check" />
              Mark Completed
            </button>
            {completeBlocker || completeError ? (
              <span className="pm-hint">{completeBlocker || completeError}</span>
            ) : null}
          </>
        )}
      </div>

      <SectionLabel icon="check">Payment</SectionLabel>
      <ReadField label="Open balance">${openBalance}</ReadField>

      <SectionLabel icon="bell">Reminders</SectionLabel>
      {job.reminders.length ? (
        job.reminders.map((r, i) => (
          <div className="reminder-item" key={i}>
            {r.text}
            <div className="meta">{fmtDate(r.date)}</div>
          </div>
        ))
      ) : (
        <div className="panel-empty">No reminders set.</div>
      )}
      <div className="toolbar-row">
        <button className="btn small" onClick={() => setRemFormOpen((v) => !v)}>
          <Icon name="bell" />
          Add reminder
        </button>
      </div>
      {remFormOpen ? (
        <div style={{ marginTop: 8 }}>
          <div className="field-row">
            <input
              type="text"
              placeholder="Reminder text"
              value={remText}
              onChange={(e) => setRemText(e.target.value)}
            />
            <input
              type="date"
              value={remDate}
              onChange={(e) => setRemDate(e.target.value)}
            />
          </div>
          <div className="toolbar-row">
            <SaveButton
              primary
              idleIcon="bell"
              label="Save"
              savedLabel="Reminder saved"
              disabled={!remText.trim()}
              onSave={() => {
                if (!remText.trim()) return false;
                actions.addJobReminder(job.id, remText.trim(), remDate || todayStr());
                setRemText("");
                setRemDate("");
              }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
